import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  X,
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
  Clock,
  Layers,
  AlertCircle,
  ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { useCan } from '@/features/auth/AuthContext'
import {
  type StageName,
  type StageProgressResult,
  type StageEventListItem,
  STAGE_DEFINITIONS,
  PRODUCTION_STAGES,
  PART_LEVEL_STAGES,
  translateStageWarning,
  translateStageError,
  generateIdempotencyKey,
  fetchStageProgress,
  fetchOrderStageEvents,
  reverseStageEvent,
} from '@/services/production/stageApi'

export interface ProductionOrderDetailDrawerProps {
  planLineId: string
  onClose: () => void
  initialProgress?: StageProgressResult | null
  initialEvents?: StageEventListItem[]
}

export function ProductionOrderDetailDrawer({
  planLineId,
  onClose,
  initialProgress = null,
  initialEvents = [],
}: ProductionOrderDetailDrawerProps) {
  const canWrite = useCan('planning.write')

  const [progress, setProgress] = useState<StageProgressResult | null>(initialProgress)
  const [loadingProgress, setLoadingProgress] = useState(() => !initialProgress)
  const [progressError, setProgressError] = useState<string | null>(null)

  // Events history
  const [events, setEvents] = useState<StageEventListItem[]>(initialEvents)
  const [loadingEvents, setLoadingEvents] = useState(() => initialEvents.length === 0)
  const [eventsError, setEventsError] = useState<string | null>(null)
  const [eventsPage, setEventsPage] = useState(1)
  const [eventsTotal, setEventsTotal] = useState(initialEvents.length)
  const [eventsTotalPages, setEventsTotalPages] = useState(1)
  const [eventsLimit] = useState(10)
  const [stageFilter, setStageFilter] = useState<StageName | ''>('')

  // Reverse event dialog
  const [reversingEvent, setReversingEvent] = useState<StageEventListItem | null>(null)
  const [reverseReason, setReverseReason] = useState('')
  const [submittingReverse, setSubmittingReverse] = useState(false)
  const [reverseError, setReverseError] = useState<string | null>(null)
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string | null>(null)

  // Load progress
  const loadProgress = useCallback(async (id: string) => {
    try {
      setLoadingProgress(true)
      setProgressError(null)
      const data = await fetchStageProgress(id)
      setProgress(data)
    } catch (err) {
      console.error('Failed to load stage progress', err)
      setProgressError(translateStageError(err))
    } finally {
      setLoadingProgress(false)
    }
  }, [])

  // Load events
  const loadEvents = useCallback(
    async (id: string, page = 1, stage?: StageName | '') => {
      try {
        setLoadingEvents(true)
        setEventsError(null)
        const res = await fetchOrderStageEvents(id, {
          page,
          limit: eventsLimit,
          stage: stage ? stage : undefined,
        })
        setEvents(res.events)
        setEventsTotal(res.pagination.total)
        setEventsTotalPages(res.pagination.total_pages)
        setEventsPage(res.pagination.page)
      } catch (err) {
        console.error('Failed to load stage events', err)
        setEventsError(translateStageError(err))
      } finally {
        setLoadingEvents(false)
      }
    },
    [eventsLimit],
  )

  useEffect(() => {
    if (planLineId) {
      if (!initialProgress) {
        void loadProgress(planLineId)
      }
      if (initialEvents.length === 0) {
        void loadEvents(planLineId, 1, stageFilter)
      }
    }
  }, [planLineId, initialProgress, initialEvents.length, stageFilter, loadProgress, loadEvents])

  // Handle execute reverse
  const handleConfirmReverse = async () => {
    if (!reversingEvent || !canWrite) return
    const reasonTrimmed = reverseReason.trim()
    if (!reasonTrimmed) {
      setReverseError('Vui lòng nhập lý do hoàn tác.')
      return
    }

    try {
      setSubmittingReverse(true)
      setReverseError(null)
      const idempotencyKey = generateIdempotencyKey()
      await reverseStageEvent(planLineId, reversingEvent.id, reasonTrimmed, idempotencyKey)

      setActionSuccessMsg(
        `Đã hoàn tác sự kiện ${STAGE_DEFINITIONS[reversingEvent.stage]?.label || reversingEvent.stage} (${reversingEvent.qty} ${STAGE_DEFINITIONS[reversingEvent.stage]?.unit || 'SP'}).`,
      )
      setReversingEvent(null)
      setReverseReason('')

      // Reload both events and progress
      await Promise.all([loadProgress(planLineId), loadEvents(planLineId, eventsPage, stageFilter)])
    } catch (err) {
      console.error('Reverse error', err)
      setReverseError(translateStageError(err))
    } finally {
      setSubmittingReverse(false)
    }
  }

  const bottleneck = progress?.bottleneck

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Chi tiết tiến độ lệnh"
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm transition-opacity"
    >
      <div className="flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl">
        {/* Header */}
        <header className="flex shrink-0 items-start justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-base font-bold text-blue-700">
                {progress?.product_code || 'Lệnh sản xuất'}
              </span>
              <span className="text-sm font-semibold text-slate-800">
                {[progress?.product_name, progress?.variant_name].filter(Boolean).join(' – ')}
              </span>
              {progress?.planned_qty != null && (
                <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-800">
                  {`KH: ${progress.planned_qty} SP`}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
              {progress?.customer_name && (
                <span>
                  Khách hàng: <strong className="text-slate-800">{progress.customer_name}</strong>
                </span>
              )}
              {progress?.order_no && (
                <span>
                  Đơn hàng: <strong className="text-slate-800">{progress.order_no}</strong>
                </span>
              )}
              <span>
                Mã lệnh: <code className="font-mono">{planLineId}</code>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link to={`/production/record?orderId=${encodeURIComponent(planLineId)}`}>
              <Button size="sm" variant="primary" className="gap-1.5 font-semibold">
                <ExternalLink className="h-4 w-4" />
                <span>Ghi nhận sản lượng</span>
              </Button>
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label="Đóng chi tiết lệnh"
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Content body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {actionSuccessMsg && (
            <div className="flex items-center justify-between rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="font-medium">{actionSuccessMsg}</span>
              </div>
              <button
                type="button"
                onClick={() => setActionSuccessMsg(null)}
                className="text-xs text-emerald-700 hover:underline"
              >
                Đóng
              </button>
            </div>
          )}

          {/* Progress loading / error */}
          {loadingProgress && !progress && (
            <div className="py-12 text-center text-sm text-slate-500">Đang tải chi tiết tiến độ lệnh...</div>
          )}

          {progressError && (
            <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <div className="flex-1">
                <p className="font-semibold text-red-900">Không thể tải tiến độ lệnh:</p>
                <p className="mt-0.5">{progressError}</p>
                <Button size="sm" variant="secondary" onClick={() => void loadProgress(planLineId)} className="mt-2">
                  Thử lại
                </Button>
              </div>
            </div>
          )}

          {progress && (
            <>
              {/* 1. Tiến độ 7 công đoạn */}
              <section aria-labelledby="section-stages-title" className="space-y-3">
                <h3 id="section-stages-title" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  1. Tiến độ 7 công đoạn sản xuất
                </h3>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                  {PRODUCTION_STAGES.map((stg) => {
                    const def = STAGE_DEFINITIONS[stg]
                    const summary = progress.stages[stg]
                    const isPart = PART_LEVEL_STAGES.includes(stg)
                    const pct = summary?.progress_pct ?? 0

                    return (
                      <div
                        key={stg}
                        className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 flex flex-col justify-between"
                      >
                        <div>
                          <div className="text-[11px] font-bold text-slate-600">{def.shortLabel}</div>
                          <div className="mt-1 text-sm font-bold text-slate-900">
                            {isPart
                              ? `${summary?.completed_units ?? 0} CT`
                              : `${summary?.completed_qty ?? 0} SP`}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {isPart
                              ? `/ ${summary?.required_units ?? 0} CT (${summary?.equivalent_sets ?? 0} bộ)`
                              : `/ ${progress.planned_qty} SP`}
                          </div>
                        </div>

                        <div className="mt-2">
                          <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
                            <span>Tiến độ</span>
                            <span className="font-bold text-slate-700">{pct}%</span>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                            <div
                              className={`h-full transition-all ${
                                pct >= 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-blue-600' : 'bg-transparent'
                              }`}
                              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>

              {/* 2. Điểm nghẽn nút thắt & Cảnh báo */}
              <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Nút thắt */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-700">
                    <Layers className="h-4 w-4 text-amber-600" />
                    <span>Nút thắt lắp ráp (Bottleneck)</span>
                  </div>

                  {bottleneck ? (
                    <div className="mt-3 space-y-1.5 text-xs text-slate-700">
                      <div className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="text-slate-500">Chi tiết nghẽn nhất:</span>
                        <strong className="text-slate-900 font-semibold">{bottleneck.part_name}</strong>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="text-slate-500">Quy cách:</span>
                        <span>{bottleneck.spec || '—'}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="text-slate-500">Định mức / SP:</span>
                        <span>{bottleneck.qty_per} cái</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="text-slate-500">Đã xong Định hình:</span>
                        <span>{bottleneck.completed_dinh_hinh} cái</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="text-slate-500">Đã dùng Lắp ráp:</span>
                        <span>{bottleneck.used_in_assembly} cái</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="text-slate-500">WIP còn sẵn sàng:</span>
                        <span className="font-semibold text-blue-700">{bottleneck.available_wip} cái</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="text-slate-500">Số bộ chi tiết đủ tối đa:</span>
                        <strong className="text-amber-800 font-bold">{bottleneck.max_sets_possible} SP</strong>
                      </div>
                      <div className="flex justify-between pt-1">
                        <span className="text-slate-500">Số chi tiết còn thiếu:</span>
                        <strong className="text-red-700 font-bold">
                          {bottleneck.deficit_units != null
                            ? bottleneck.deficit_units > 0
                              ? `Thiếu ${bottleneck.deficit_units} cái`
                              : 'Đã đủ bộ'
                            : bottleneck.max_sets_possible >= progress.planned_qty
                              ? 'Đã đủ bộ'
                              : `Thiếu cho ${progress.planned_qty - bottleneck.max_sets_possible} SP`}
                        </strong>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 text-center text-xs text-slate-500">
                      Chưa có dữ liệu chi tiết định mức cho lệnh này.
                    </div>
                  )}
                </div>

                {/* Danh sách cảnh báo */}
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-700">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span>Cảnh báo dòng chảy ({progress.warnings.length})</span>
                    </div>
                  </div>

                  {progress.warnings.length === 0 ? (
                    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-emerald-700 py-3 bg-emerald-50 rounded-md">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <span>Không có cảnh báo dòng chảy. Các công đoạn bình thường.</span>
                    </div>
                  ) : (
                    <ul className="mt-3 divide-y divide-slate-100 max-h-48 overflow-y-auto pr-1">
                      {progress.warnings.map((w, idx) => (
                        <li key={idx} className="py-2 text-xs leading-relaxed text-amber-900">
                          <span className="font-semibold">• {translateStageWarning(w)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              {/* 3. Bảng chi tiết định mức mộc */}
              <section aria-labelledby="section-parts-title" className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 id="section-parts-title" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    2. Bảng chi tiết mộc ({progress.parts?.length || 0} chi tiết)
                  </h3>
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                  <table className="w-full border-collapse text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-700 uppercase">
                        <th className="px-3 py-2 text-center w-12">STT</th>
                        <th className="px-3 py-2">Tên chi tiết</th>
                        <th className="px-3 py-2">Quy cách (T×W×L)</th>
                        <th className="px-3 py-2">Loại gỗ</th>
                        <th className="px-3 py-2 text-right">Định mức/SP</th>
                        <th className="px-3 py-2 text-right">Kế hoạch</th>
                        <th className="px-3 py-2 text-right">Đã Phôi</th>
                        <th className="px-3 py-2 text-right">Đã Đ.Hình</th>
                        <th className="px-3 py-2 text-right">WIP sẵn sàng</th>
                        <th className="px-3 py-2 text-right">Bộ đủ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(!progress.parts || progress.parts.length === 0) ? (
                        <tr>
                          <td colSpan={10} className="px-4 py-6 text-center text-xs text-slate-500">
                            Chưa có danh sách chi tiết mộc. Cần người có quyền ghi mở lệnh để nạp bản chụp.
                          </td>
                        </tr>
                      ) : (
                        progress.parts.map((p, idx) => {
                          const isBottleneck = bottleneck?.part_snapshot_id === p.part_snapshot_id
                          return (
                            <tr
                              key={p.part_snapshot_id || idx}
                              className={`hover:bg-slate-50 ${isBottleneck ? 'bg-amber-50/70 font-medium' : ''}`}
                            >
                              <td className="px-3 py-2 text-center text-slate-500">{idx + 1}</td>
                              <td className="px-3 py-2 font-medium text-slate-900">
                                {p.part_name}
                                {isBottleneck && (
                                  <span className="ml-1.5 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">
                                    Nút thắt
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-slate-600 font-mono text-[11px]">{p.spec || '—'}</td>
                              <td className="px-3 py-2 text-slate-600">{p.wood_type || '—'}</td>
                              <td className="px-3 py-2 text-right font-mono text-slate-700">{p.qty_per}</td>
                              <td className="px-3 py-2 text-right font-mono font-medium text-slate-900">{p.target_qty}</td>
                              <td className="px-3 py-2 text-right font-mono text-slate-700">{p.cum_phoi}</td>
                              <td className="px-3 py-2 text-right font-mono text-slate-700">{p.cum_dinh_hinh}</td>
                              <td className="px-3 py-2 text-right font-mono font-bold text-blue-700">{p.wip_available}</td>
                              <td className="px-3 py-2 text-right font-mono font-bold text-slate-800">{p.eligible_sets}</td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {/* 4. Lịch sử sự kiện ghi nhận & Nút đảo */}
          <section aria-labelledby="section-events-title" className="space-y-3 pt-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                <h3 id="section-events-title" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  3. Lịch sử sự kiện sản lượng ({eventsTotal})
                </h3>
              </div>

              {/* Lọc theo công đoạn */}
              <div className="flex items-center gap-2">
                <select
                  aria-label="Lọc công đoạn sự kiện"
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value as any)}
                  className="rounded-md border border-slate-300 py-1 px-2.5 text-xs focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Tất cả công đoạn</option>
                  {PRODUCTION_STAGES.map((s) => (
                    <option key={s} value={s}>
                      {STAGE_DEFINITIONS[s].shortLabel}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loadingEvents ? (
              <div className="py-6 text-center text-xs text-slate-500">Đang tải lịch sử sự kiện...</div>
            ) : eventsError ? (
              <div className="rounded-md bg-red-50 p-3 text-xs text-red-700">{eventsError}</div>
            ) : events.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500">
                Chưa có lượt ghi nhận sản lượng nào cho lệnh này.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-700 uppercase">
                      <th className="px-3 py-2">Thời gian</th>
                      <th className="px-3 py-2">Ngày ghi</th>
                      <th className="px-3 py-2">Công đoạn</th>
                      <th className="px-3 py-2">Chi tiết</th>
                      <th className="px-3 py-2 text-right">Số lượng</th>
                      <th className="px-3 py-2">Người ghi</th>
                      <th className="px-3 py-2">Ghi chú</th>
                      <th className="px-3 py-2 text-center">Trạng thái</th>
                      {canWrite && <th className="px-3 py-2 text-center w-20">Thao tác</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {events.map((ev) => {
                      const stageDef = STAGE_DEFINITIONS[ev.stage]
                      const isReversal = ev.is_reversal
                      const isReversedByOther = Boolean(ev.reversed_by_event_id)

                      return (
                        <tr
                          key={ev.id}
                          className={`hover:bg-slate-50 ${isReversal ? 'bg-orange-50/50' : isReversedByOther ? 'bg-slate-50/80 text-slate-400' : ''}`}
                        >
                          <td className="px-3 py-2 font-mono text-[11px] text-slate-500">
                            {new Date(ev.created_at).toLocaleString('vi-VN', {
                              hour: '2-digit',
                              minute: '2-digit',
                              day: '2-digit',
                              month: '2-digit',
                            })}
                          </td>
                          <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{ev.production_date}</td>
                          <td className="px-3 py-2 font-medium text-slate-900">{stageDef?.label || ev.stage}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {ev.part_name ? (
                              <span>
                                {ev.part_name} {ev.spec && <span className="font-mono text-slate-500">({ev.spec})</span>}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-bold">
                            <span className={isReversal ? 'text-orange-700' : 'text-slate-900'}>
                              {isReversal ? `-${ev.qty}` : `+${ev.qty}`} {stageDef?.unit || 'SP'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-600">{ev.actor}</td>
                          <td className="px-3 py-2 text-slate-500 max-w-xs truncate" title={ev.note || ''}>
                            {ev.note || '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {isReversal ? (
                              <Badge tone="warning" className="px-2 py-0.5 text-[10px]">
                                Sự kiện đảo
                              </Badge>
                            ) : isReversedByOther ? (
                              <Badge tone="neutral" className="px-2 py-0.5 text-[10px] line-through">
                                Đã bị đảo
                              </Badge>
                            ) : (
                              <Badge tone="success" className="px-2 py-0.5 text-[10px]">
                                Hiệu lực
                              </Badge>
                            )}
                          </td>
                          {canWrite && (
                            <td className="px-3 py-2 text-center">
                              {ev.can_reverse ? (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setReversingEvent(ev)
                                    setReverseReason('')
                                    setReverseError(null)
                                  }}
                                  className="h-7 px-2 text-xs font-semibold text-orange-700 hover:bg-orange-50 border-orange-300"
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  <span>Đảo</span>
                                </Button>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination for events */}
            {eventsTotalPages > 1 && (
              <div className="flex items-center justify-between text-xs text-slate-600 pt-2">
                <span>
                  Trang {eventsPage} / {eventsTotalPages} ({eventsTotal} sự kiện)
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={eventsPage <= 1}
                    onClick={() => void loadEvents(planLineId, eventsPage - 1, stageFilter)}
                  >
                    Trước
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={eventsPage >= eventsTotalPages}
                    onClick={() => void loadEvents(planLineId, eventsPage + 1, stageFilter)}
                  >
                    Sau
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <footer className="flex shrink-0 items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
          <span className="text-xs text-slate-500">Mộc Phát ERP • Theo dõi sản lượng 7 công đoạn</span>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </footer>
      </div>

      {/* Confirmation Modal for Reverse Event */}
      {reversingEvent && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reverse-modal-title"
          className="fixed inset-0 z-60 flex items-center justify-center bg-slate-900/60 p-4"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-2 text-orange-600 font-bold text-base">
              <RotateCcw className="h-5 w-5" />
              <h4 id="reverse-modal-title">Xác nhận hoàn tác (Đảo sự kiện)</h4>
            </div>

            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700 space-y-1">
              <div>
                <span className="text-slate-500">Công đoạn:</span>{' '}
                <strong className="text-slate-900">
                  {STAGE_DEFINITIONS[reversingEvent.stage]?.label || reversingEvent.stage}
                </strong>
              </div>
              {reversingEvent.part_name && (
                <div>
                  <span className="text-slate-500">Chi tiết:</span>{' '}
                  <strong className="text-slate-900">
                    {reversingEvent.part_name} {reversingEvent.spec && `(${reversingEvent.spec})`}
                  </strong>
                </div>
              )}
              <div>
                <span className="text-slate-500">Số lượng hoàn tác:</span>{' '}
                <strong className="text-orange-700">
                  {reversingEvent.qty} {STAGE_DEFINITIONS[reversingEvent.stage]?.unit || 'SP'}
                </strong>
              </div>
              <div>
                <span className="text-slate-500">Ngày ghi:</span>{' '}
                <span>{reversingEvent.production_date}</span>
              </div>
            </div>

            <div>
              <label htmlFor="reverse-reason-input" className="block text-xs font-bold text-slate-700 mb-1">
                Lý do hoàn tác <span className="text-red-600">*</span>:
              </label>
              <textarea
                id="reverse-reason-input"
                rows={3}
                placeholder="Nhập lý do đảo sự kiện (bắt buộc)..."
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2 text-xs focus:border-blue-500 focus:outline-none"
              />
            </div>

            {reverseError && (
              <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{reverseError}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setReversingEvent(null)
                  setReverseReason('')
                  setReverseError(null)
                }}
                disabled={submittingReverse}
              >
                Hủy
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleConfirmReverse()}
                disabled={submittingReverse || !reverseReason.trim()}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {submittingReverse ? 'Đang hoàn tác...' : 'Xác nhận đảo'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
